import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
    const isChunkError = error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Failed to fetch dynamically imported module');
    // Safari surfaces chunk load failures as plain "Failed to fetch" without the module URL.
    // Guard with sessionStorage to avoid an infinite reload loop if the chunk is genuinely broken.
    const isSafariChunkError = error?.message === 'Failed to fetch' && !sessionStorage.getItem('go4_chunk_reload_v1');
    if (isChunkError || isSafariChunkError) {
      if (isSafariChunkError) sessionStorage.setItem('go4_chunk_reload_v1', '1');
      window.location.reload();
      return;
    }
    // Dynamic import — keeps Sentry SDK out of the main bundle.
    if (process.env.NODE_ENV === 'production') {
      import('@sentry/react').then(Sentry => {
        Sentry.withScope(scope => {
          scope.setExtras({ componentStack: info?.componentStack });
          Sentry.captureException(error);
        });
      }).catch(() => { /* Sentry import failed — error already logged to console */ });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { title = 'Something went wrong', minimal = false } = this.props;

    if (minimal) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-base font-black text-slate-700 dark:text-slate-300 mb-2">{title}</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mb-2">
            This section failed to load. Try refreshing the page.
          </p>
          {this.state.error && (
            <p className="text-[10px] font-mono text-slate-300 dark:text-slate-600 mb-4 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-xl max-w-sm truncate">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-5 py-2 bg-rose-600 text-white font-black text-xs rounded-xl hover:bg-rose-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <div className="text-8xl mb-6">😵</div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
            Oops! Something broke.
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-2 leading-relaxed">
            An unexpected error occurred. Your study progress is safe — this is just a display issue.
          </p>
          {this.state.error && (
            <p className="text-[10px] font-mono text-slate-300 dark:text-slate-600 mb-6 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-xl truncate">
              {this.state.error.message}
            </p>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-6 py-2.5 bg-rose-600 text-white font-black text-sm rounded-xl hover:bg-rose-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-black text-sm rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
